import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import clearForm from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/ClearForm";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isVmiAdmin, isVmiUser } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductDescription, { ProductDescriptionStyles } from "@tCooneyBrothers/Overrides/Components/ProductDescription";

import CustomContactUsForm from "@tCooneyBrothers/Components/CustomContactUsForm";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
    target?: string;
    brandName?: string | undefined;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const settings = getSettingsCollection(state);
    return {
        wishListSettings: settings.wishListSettings,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        canViewVmiOrder: isVmiAdmin(settings.orderSettings, session) || isVmiUser(settings.orderSettings, session),
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardProductAvailabiltyGridItem?: GridItemProps;

    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: LinkPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    withoutQTYshipOrderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyOrderedGridItem?: GridItemProps;
    withoutQTYshipProductInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    withoutQTYshipProductInfoSubtotalGridItem?: GridItemProps;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    withoutQTYshipProductInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardAddToListButton: {
        css: css`
            span {
                text-transform: uppercase;
                line-height: initial;
                padding-top: 3px;
                font-weight: 600;
                font-size: 11px;
            }
            [data-test-selector="text"] {
                text-transform: uppercase;
            }
        `,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: [12, 12, 2, 1, 1],
        css: css`
            justify-content: center;
            align-items: center;
            img {
                height: 90px;
                width: auto;
            }
            // @media print {
            //     max-height: 83px;
            //     max-width: 83px;
            //     padding: 0;
            // }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [12, 12, 7, 9, 9],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [12, 12, 3, 2, 2],
        printWidth: 0,
        css: css`
            align-items: flex-end;
            justify-content: flex-end;
            .product-Availability-text-container {
                font-size: 11px;
                text-align: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            align-items: flex-end;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    orderLineCardProductAvailabiltyGridItem: {
        width: [12, 12, 3, 2, 2],
        printWidth: 0,
        css: css`
            justify-content: center;
            align-items: center;
            padding: 0px;
            .product-Availability-text-container {
                font-size: 11px;
                text-align: center;
            }
        `,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 6, 6],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 10,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
        css: css`
            padding-right: 10px;
        `,
    },
    productInfoErpNumberGridItem: {
        width: [6, 4, 4, 4, 4],
    },
    productInfoManufacturerItemGridItem: {
        width: [6, 4, 4, 4, 4],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [6, 4, 4, 4, 4],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 6, 6],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [6, 4, 4, 3, 3],
        css: css`
            order: 3;
            text-transform: uppercase;
            [data-test-selector="text"] {
                text-transform: uppercase;
                white-space: normal;
            }
        `,
    },
    withoutQTYshipOrderLinePriceGridItem: {
        width: [6, 4, 4, 4, 4],
        css: css`
            [data-test-selector="text"] {
                text-transform: uppercase;
                white-space: normal;
            }
        `,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 4, 4, 3, 3],
        printWidth: 4,
        css: css`
            order: 1;
            padding-right: 0;
        `,
    },
    withoutQTYshipProductInfoQtyOrderedGridItem: {
        width: [6, 4, 4, 4, 4],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 4, 4, 3, 3],
        printWidth: 4,
        css: css`
            order: 2;
        `,
    },
    productInfoSubtotalGridItem: {
        width: [6, 4, 4, 3, 3],
        printWidth: 4,
        css: css`
            order: 4;
        `,
    },
    withoutQTYshipProductInfoSubtotalGridItem: {
        width: [12, 4, 4, 4, 4],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: 12,
    },
    withoutQTYshipProductInfoTotalsGridItem: {
        width: 12,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
    canViewVmiOrder = false,
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    canViewVmiOrder?: boolean;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <GridItem {...styles.productInfoGridItem}>
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem}>
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {/* {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )} */}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            {canViewVmiOrder && vmiLocationId && orderLine.isActiveVmiBin && (
                                <VmiBinDetailTypeLink
                                    title={orderLine.shortDescription}
                                    locationId={vmiLocationId}
                                    id={orderLine.vmiBinId}
                                />
                            )}
                            {canViewVmiOrder && vmiLocationId && !orderLine.isActiveVmiBin && (
                                <Typography as="p">{orderLine.shortDescription}</Typography>
                            )}
                            {(!canViewVmiOrder || !vmiLocationId) && (
                                <ProductDescription
                                    product={orderLine}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem}>
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem}>
                    <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                        {orderLine.customerProductNumber && (
                            <GridItem {...styles.productInfoCustomerProductGridItem}>
                                <SmallHeadingAndText
                                    {...styles.customerProductNumberHeadingAndText}
                                    heading={translate("My Part #")}
                                    text={orderLine.customerProductNumber}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoErpNumberGridItem}>
                            <SmallHeadingAndText
                                {...styles.erpNumberHeadingAndText}
                                heading={translate("Item #")}
                                text={orderLine.productErpNumber}
                            />
                        </GridItem>
                        {orderLine.manufacturerItem && (
                            <GridItem {...styles.productInfoManufacturerItemGridItem}>
                                <SmallHeadingAndText
                                    {...styles.manufacturerHeadingAndText}
                                    heading={translate("MFG #")}
                                    text={orderLine.manufacturerItem}
                                />
                            </GridItem>
                        )}
                        {orderLine.vmiBinNumber && (
                            <GridItem {...styles.productInfoVmiBinNumberGridItem}>
                                <SmallHeadingAndText
                                    {...styles.vmiBinNumberHeadingAndText}
                                    heading={translate("Bin #")}
                                    text={orderLine.vmiBinNumber}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    showPricing: state.context.showPricing,
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    styles,
    showPricing,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
}) => {
    let promotions: JSX.Element[] = [];
    const OPEN_ORDER = "Open Order";
    const INVOICED = "Invoiced";
    const BID = "Bid";
    const orderStatus = order.status;
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }
    // console.log("QTY shipped", order);
    return (
        <>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoGridContainer}>
                    {promotions.length > 0 && (
                        <GridItem {...styles.orderLineInfoPromotionList}>
                            <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                        </GridItem>
                    )}
                    <GridItem
                        {...(orderStatus !== BID
                            ? { ...styles.productInfoTotalsGridItem }
                            : { ...styles.withoutQTYshipProductInfoTotalsGridItem })}
                    >
                        <GridContainer {...styles.productInfoTotalsGridContainer}>
                            <GridItem
                                {...(orderStatus !== BID
                                    ? { ...styles.productInfoQtyOrderedGridItem }
                                    : { ...styles.withoutQTYshipProductInfoQtyOrderedGridItem })}
                            >
                                <SmallHeadingAndText
                                    {...styles.productInfoQtyOrderedHeadingAndText}
                                    heading={translate("QTY Ordered")}
                                    data-test-selector="qty_Ordered"
                                    text={`${orderLine.qtyOrdered}`}
                                />
                            </GridItem>
                            {orderStatus !== BID && (
                                <GridItem {...styles.productInfoQtyShippedGridItem}>
                                    <SmallHeadingAndText
                                        {...styles.productInfoQtyShippedHeadingAndText}
                                        heading={
                                            orderStatus === OPEN_ORDER
                                                ? translate("QTY TO SHIP")
                                                : translate("QTY Shipped")
                                        }
                                        text={`${orderLine.qtyShipped}`}
                                    />
                                </GridItem>
                            )}
                            <GridItem
                                {...(orderStatus !== BID
                                    ? { ...styles.orderLinePriceGridItem }
                                    : { ...styles.withoutQTYshipOrderLinePriceGridItem })}
                            >
                                <SmallHeadingAndText
                                    {...styles.priceHeadingAndText}
                                    heading={translate("Price")}
                                    text={
                                        orderLine.unitPriceDisplay +
                                        (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                                    }
                                />
                            </GridItem>
                            <GridItem
                                {...(orderStatus !== BID
                                    ? { ...styles.productInfoSubtotalGridItem }
                                    : { ...styles.withoutQTYshipProductInfoSubtotalGridItem })}
                            >
                                <SmallHeadingAndText
                                    {...styles.productInfoSubtotalHeadingAndText}
                                    heading={translate("EXT Price")}
                                    text={orderLine.totalRegularPriceDisplay}
                                    // text={orderLine.unitPriceDisplay * orderLine.qtyShipped}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </>
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    canViewVmiOrder?: boolean;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        canViewVmiOrder = false,
    } = props;
    const [isContactUsPopUpEnable, setIsContactUsPopUpEnable] = React.useState(false);
    const contactUsModalCloseHandler = () => {
        setIsContactUsPopUpEnable(false);
    };
    const contactUsModalOpenHandler = () => {
        setIsContactUsPopUpEnable(true);
    };
    const continueButtonClickHandlerToParent = (modalState: boolean) => {
        modalState = false;
        setIsContactUsPopUpEnable(modalState);
        clearForm();
    };
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const isShareEntity = getIsShareEntity();
    const hasVmi = orderLine.isActiveProduct && !!order.vmiLocationId && orderLine.isActiveVmiBin;
    const hasProduct = orderLine.isActiveProduct && !!orderLine.productUri;
    const showProductLink = (!canViewVmiOrder && hasProduct) || (canViewVmiOrder && !hasVmi && hasProduct);
    const showVmiLink = canViewVmiOrder && hasVmi;
    const showNoLink = !showProductLink && !showVmiLink;

    return (
        <>
            <StyledWrapper>
                <Modal
                    headline={translate("Contact Us")}
                    contentLabel="Contact Us"
                    {...styles.successModal}
                    isOpen={isContactUsPopUpEnable}
                    handleClose={contactUsModalCloseHandler}
                >
                    <GridContainer {...styles.modalContainer}>
                        <GridItem {...styles.messageTextGridItem}>
                            <CustomContactUsForm
                                productQty={orderLine.qtyOrdered}
                                productTitle={orderLine.description}
                                productNumber={orderLine.productErpNumber}
                                availability={orderLine.availability}
                                orderNumber={order.webOrderNumber}
                                parentModalState={continueButtonClickHandlerToParent}
                                brandName={undefined}
                            />
                        </GridItem>
                    </GridContainer>
                </Modal>
            </StyledWrapper>
            <GridContainer
                {...styles.orderLineCardGridContainer}
                data-test-selector={`orderLineCard${orderLine.productId}`}
            >
                <GridItem {...styles.orderLineCardImageGridItem}>
                    {showProductLink && (
                        <Link {...styles.productImageLink} href={orderLine.productUri}>
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={orderLine.mediumImagePath}
                                isShareEntity={isShareEntity}
                            />
                        </Link>
                    )}
                    {showVmiLink && (
                        <Link
                            {...styles.productImageLink}
                            href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                        >
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={orderLine.mediumImagePath}
                                isShareEntity={isShareEntity}
                            />
                        </Link>
                    )}
                    {showNoLink && (
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    )}
                </GridItem>
                <GridItem {...styles.orderLineCardInfoGridItem}>
                    <GridContainer {...styles.orderLineCardInfoGridContainer}>
                        <OrderLineProductInfo
                            orderLine={orderLine}
                            styles={styles}
                            vmiLocationId={order.vmiLocationId}
                            canViewVmiOrder={canViewVmiOrder}
                        />
                        <OrderLineInfo orderLine={orderLine} order={order} styles={styles} />
                        <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
                    </GridContainer>
                </GridItem>
                <GridItem
                    {...(orderLine.properties.productDefinedInInsite === "false"
                        ? { ...styles.orderLineCardProductAvailabiltyGridItem }
                        : { ...styles.orderLineCardAddToListGridItem })}
                >
                    {orderLine.canAddToWishlist ? (
                        <Link {...styles.orderLineCardAddToListButton} onClick={addToListClickHandler}>
                            {translate("Add to List")}
                        </Link>
                    ) : (
                        orderLine.properties.productDefinedInInsite === "false" && (
                            <StyledWrapper className="product-Availability-text-container">
                                This item cannot be reordered on the website at this time. Please&nbsp;
                                <Link onClick={() => contactUsModalOpenHandler()}>contact us</Link>
                                &nbsp;for current pricing & availability.
                            </StyledWrapper>
                        )
                    )}
                </GridItem>
            </GridContainer>
        </>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    canViewVmiOrder,
    vmiBinDetailsPageUrl,
    brandName,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <GridContainer {...styles.gridContainer}>
            {order.orderLines.map(orderLine => (
                <CardContainer {...styles.orderLineCardContainer} key={orderLine.id}>
                    <GridItem {...styles.orderLineCardGridItem}>
                        <OrderLineCard
                            orderLine={orderLine}
                            order={order}
                            wishListSettings={wishListSettings}
                            styles={styles}
                            canViewVmiOrder={canViewVmiOrder}
                            setAddToListModalIsOpen={setAddToListModalIsOpen}
                            addToWishList={addToWishList}
                            vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                        />
                    </GridItem>
                </CardContainer>
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
